import {
	AlertToast,
	SuccessToast,
	WarningToast,
} from "@/shared/notifications/AlertToast";
import {
	getAlertTheme,
	withAlertTheme,
} from "@/shared/notifications/alertTheme";
import Swal from "sweetalert2";

import { OrganizationModulesRequest } from "@/model/Modules/OrganizationModules";

type Callback = (request: OrganizationModulesRequest) => void;

export const Alert = (
	callback: Callback,
	isActive: boolean,
	moduleName: string,
) => {
	const request: OrganizationModulesRequest = {
		moduleName,
		activated: !isActive,
	};
	if (!isActive) {
		AlertSuccess(moduleName);
		callback(request);
	} else {
		AlertConfirm(callback, request);
	}
};

const AlertSuccess = (moduleName: string) => {
	void SuccessToast(
		`You now have access to ${moduleName}`,
		"You can use this module from the menu.",
	);
};

export const AlertConfirmDeleteComment = (
	callback: () => void,
	commentId: number,
) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Delete this comment?",
				text: `Comment #${commentId} will be permanently removed.`,
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete comment",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				return true;
			}
		})
		.catch(() => {
			throw new Error("Error");
			return false;
		});
};

const AlertConfirm = (
	callback: Callback,
	request: OrganizationModulesRequest,
) => {
	const theme = getAlertTheme();
	void Swal.fire(
		withAlertTheme(
			{
				title: `Disable ${request.moduleName}?`,
				text: "You can turn it back on later from settings.",
				icon: "question",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Disable",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	).then((result) => {
		if (result.isConfirmed) {
			callback(request);
			void SuccessToast(
				"Module disabled",
				`${request.moduleName} has been turned off.`,
			);
		}
	});
};

export const AlertWarning = (moduleName: string) => {
	void WarningToast(
		`${moduleName} is not available yet`,
		"Contact your administrator or SATOS for more information.",
	);
};

export const AlertConfirmStepOverride = (callback: () => void) => {
	const theme = getAlertTheme();
	void Swal.fire(
		withAlertTheme(
			{
				title: "Override this step?",
				text: "You are confirming that the value is out of range. This will mark the step as overridden.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.confirmColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Override step",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				void SuccessToast(
					"Step overridden",
					"The step was marked as overridden.",
				);
			}
		})
		.catch(() => {
			void AlertToast.failed();
		});
};

export const AlertConfirmDelete = (callback: () => void, name: string) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Delete this item?",
				text: `${name} will be permanently removed. This cannot be undone.`,
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				return true;
			}
		})
		.catch(() => {
			throw new Error("Error");
			return false;
		});
};

/** Delete checklist: explicit warning that this removes the entire checklist and all steps.
 * Set showSuccessToast=false when using soft-undo (snackbar with Undo) instead. */
export const AlertConfirmDeleteChecklist = (
	callback: () => void,
	checklistName: string,
	stepCount: number,
	showSuccessToast = true,
) => {
	const theme = getAlertTheme();
	const stepText =
		stepCount === 0
			? "This will permanently delete the entire checklist."
			: stepCount === 1
				? "This will permanently delete the checklist and its 1 step."
				: `This will permanently delete the checklist and all ${stepCount} steps.`;
	const name = checklistName || "This checklist";
	Swal.fire(
		withAlertTheme(
			{
				title: "Delete entire checklist?",
				html: `<p><strong>${escapeHtml(name)}</strong></p><p>${escapeHtml(stepText)} This cannot be undone.</p>`,
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete checklist",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				if (showSuccessToast) {
					void AlertToast.success();
				}
			}
		})
		.catch(() => {
			void AlertToast.failed();
		});
};

function escapeHtml(s: string): string {
	const div = document.createElement("div");
	div.textContent = s;
	return div.innerHTML;
}

export const AlertConfirmDeleteShiftEvent = (
	callback: () => void,
	shiftEventId: number,
) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Delete this shift event?",
				text: `Shift event #${shiftEventId} will be permanently removed.`,
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				return true;
			}
		})
		.catch(() => {
			throw new Error("Error");
			return false;
		});
};

export const AlertConfirmDeleteMeasurementEdit = (
	callback: () => void,
	measurementId: number,
) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Delete this measurement point?",
				text: `Measurement #${measurementId} will be permanently removed.`,
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				return true;
			}
		})
		.catch(() => {
			throw new Error("Error");
			return false;
		});
};

export const AlertConfirmDeleteTask = (callback: () => void) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Delete this task?",
				text: "The task will be permanently removed. This cannot be undone.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete task",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				void AlertToast.success();
			}
		})
		.catch(() => {
			void AlertToast.failed();
		});
};

export const AlertConfirmCompleteTask = (callback: () => void) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Mark this task as complete?",
				text: "The task will be marked as done.",
				icon: "question",
				showCancelButton: true,
				confirmButtonColor: theme.confirmColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Complete task",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				void AlertToast.success();
			}
		})
		.catch(() => {
			void AlertToast.failed();
		});
};

export const AlertConfirmDeleteGroup = (callback: () => void) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Delete this group?",
				text: "The group will be permanently removed. This cannot be undone.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete group",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				void AlertToast.success();
			}
		})
		.catch(() => {
			void AlertToast.failed();
		});
};
export const AlertConfirmDeleteMeasurement = (callback: () => void) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Delete this measurement?",
				text: "The measurement will be permanently removed. This cannot be undone.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete measurement",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				void AlertToast.success();
			}
		})
		.catch(() => {
			void AlertToast.failed();
		});
};

export const AlertConfirmDeleteSample = (callback: () => void) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Delete this sample?",
				text: "The sample will be permanently removed. This cannot be undone.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete sample",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				void AlertToast.success();
			}
		})
		.catch(() => {
			void AlertToast.failed();
		});
};
export const AlertConfirmDeleteAnalysis = (callback: () => void) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Delete this analysis?",
				text: "The analysis will be permanently removed. This cannot be undone.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete analysis",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				void AlertToast.success();
			}
		})
		.catch(() => {
			void AlertToast.failed();
		});
};

export const AlertConfirmDeleteCourse = (callback: () => void) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Save changes?",
				text: "You have unsaved changes. Do you want to save before leaving?",
				icon: "question",
				showDenyButton: true,
				confirmButtonText: "Save",
				denyButtonText: "Don't save",
				confirmButtonColor: theme.confirmColor,
				denyButtonColor: theme.denyColor,
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				void AlertToast.success();
			}
		})
		.catch(() => {
			void AlertToast.failed();
		});
};

export const AlertSnow = (callback: () => void) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Let it snow",
				text: "Add a festive snow effect to the screen?",
				showDenyButton: true,
				showCancelButton: true,
				confirmButtonText: "Yes, let it snow",
				denyButtonText: "No thanks",
				cancelButtonText: "Cancel",
				confirmButtonColor: theme.confirmColor,
				denyButtonColor: theme.denyColor,
				cancelButtonColor: theme.cancelColor,
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				void AlertToast.success("Snow effect enabled. Enjoy!");
			} else if (result.isDenied) {
				void WarningToast("Snow effect cancelled.", "");
			}
		})
		.catch(() => {
			void AlertToast.failed();
		});
};

export const AlertConfirmDeleteMedia = (callback: () => void) => {
	const theme = getAlertTheme();
	void Swal.fire(
		withAlertTheme(
			{
				title: "Delete this media?",
				text: "The media file will be permanently removed. This cannot be undone.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete media",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				void AlertToast.success();
			}
		})
		.catch(() => {
			void AlertToast.failed();
		});
};

export const AlertConfirmDeleteQuestion = (callback: () => void) => {
	const theme = getAlertTheme();
	Swal.fire(
		withAlertTheme(
			{
				title: "Delete this question?",
				text: "The question will be permanently removed. This cannot be undone.",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: theme.errorColor,
				cancelButtonColor: theme.cancelColor,
				confirmButtonText: "Delete question",
				cancelButtonText: "Cancel",
			},
			theme,
		),
	)
		.then((result) => {
			if (result.isConfirmed) {
				callback();
				void AlertToast.success();
			}
		})
		.catch(() => {
			void AlertToast.failed();
		});
};
